import { BaseModule } from "./baseModule";
import type { AxiosInstance } from "axios";
import type { ErrorHandler } from "./errorHandler";
import type {
  CandidateApplication,
  CandidateWithApplications,
  CandidateProfile,
  CandidateStage,
  UserVideo,
  YouTubeVideo,
  LinkYouTubeVideoRequest,
  EmailHistoryEntry,
  MessageResponse,
  RequestConfig,
} from "./types";

/**
 * Candidates Module - Handles all candidate management API operations
 *
 * This module provides candidate management operations including stage tracking,
 * email communication, and bidirectional access (job-centric and candidate-centric views).
 */
export class CandidatesModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  /**
   * Approve candidate for next stage
   * POST /api/admin/jobs/:jobId/candidates/:candidateId/approve
   */
  async approveForNextStage(
    jobId: string,
    candidateId: string,
    notes?: string,
    config?: RequestConfig,
  ): Promise<CandidateApplication> {
    return this.post<CandidateApplication>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/approve`,
      { notes },
      config,
    );
  }

  /**
   * Reject candidate
   * POST /api/admin/jobs/:jobId/candidates/:candidateId/reject
   */
  async rejectCandidate(
    jobId: string,
    candidateId: string,
    reason: string,
    config?: RequestConfig,
  ): Promise<CandidateApplication> {
    return this.post<CandidateApplication>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/reject`,
      { reason },
      config,
    );
  }

  /**
   * Send email to candidate
   * POST /api/admin/jobs/:jobId/candidates/:candidateId/email
   */
  async sendCandidateEmail(
    jobId: string,
    candidateId: string,
    emailData: {
      subject: string;
      content: string;
      cc?: string[];
    },
    config?: RequestConfig,
  ): Promise<MessageResponse> {
    return this.post<MessageResponse>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/email`,
      emailData,
      config,
    );
  }

  /**
   * Get candidate email history for a specific job
   * GET /api/admin/jobs/:jobId/candidates/:candidateId/email-history
   */
  async getCandidateEmailHistory(
    jobId: string,
    candidateId: string,
    config?: RequestConfig,
  ): Promise<EmailHistoryEntry[]> {
    return this.get<EmailHistoryEntry[]>(
      `/api/admin/jobs/${jobId}/candidates/${candidateId}/email-history`,
      config,
    );
  }

  // ============================================================================
  // Candidate-Centric Operations
  // ============================================================================

  /**
   * Map backend status to frontend stage name
   */
  private statusToStage(status: string): CandidateStage {
    const statusMap: Record<string, CandidateStage> = {
      submitted: "Applied",
      reviewed: "Resume Review",
      shortlisted: "Shortlisted",
      interview_scheduled: "Interview Scheduled",
      interviewed: "Interview Completed",
      offered: "Offer Extended",
      hired: "Hired",
      rejected: "Rejected",
      withdrawn: "Applied",
    };
    return statusMap[status] || "Applied";
  }

  /**
   * Get all applications for a specific candidate (across all jobs)
   * GET /api/admin/candidates/:candidateId
   */
  async getCandidateApplications(
    candidateId: string,
    config?: RequestConfig,
  ): Promise<CandidateWithApplications> {
    // The backend returns CandidateProfile directly, we need to wrap it
    const candidateProfile = await this.get<CandidateProfile>(
      `/api/admin/candidates/${candidateId}`,
      config,
    );

    // Transform applications to CandidateApplication format
    const applications = (candidateProfile.applications || []).map((app) => ({
      id: app.id,
      job_id: app.job_id,
      candidate_id: app.user_id || candidateId, // Backend uses user_id
      resume_id: app.resume_id,
      current_stage: this.statusToStage(app.status), // Use current_stage if available, otherwise map from status
      stage_history: [], // Include if available
      applied_at: app.applied_at,
      updated_at: app.updated_at || app.applied_at,
    }));

    // Transform to CandidateWithApplications format
    return {
      candidate: candidateProfile,
      applications,
    };
  }

  /**
   * Move candidate to next stage in the workflow
   */
  async advanceToNextStage(
    jobId: string,
    candidateId: string,
    notes?: string,
    config?: RequestConfig,
  ): Promise<CandidateApplication> {
    return this.approveForNextStage(jobId, candidateId, notes, config);
  }

  /**
   * Bulk approve candidates for next stage
   */
  async bulkApproveForNextStage(
    jobId: string,
    candidateIds: string[],
    notes?: string,
    config?: RequestConfig,
  ): Promise<CandidateApplication[]> {
    const promises = candidateIds.map((candidateId) =>
      this.approveForNextStage(jobId, candidateId, notes, config),
    );

    return Promise.all(promises);
  }

  /**
   * Bulk reject candidates
   */
  async bulkRejectCandidates(
    jobId: string,
    candidateIds: string[],
    reason: string,
    config?: RequestConfig,
  ): Promise<CandidateApplication[]> {
    const promises = candidateIds.map((candidateId) =>
      this.rejectCandidate(jobId, candidateId, reason, config),
    );

    return Promise.all(promises);
  }

  /**
   * Send bulk emails to candidates
   */
  async bulkSendEmails(
    jobId: string,
    candidateIds: string[],
    emailData: {
      subject: string;
      content: string;
      cc?: string[];
    },
    config?: RequestConfig,
  ): Promise<MessageResponse[]> {
    const promises = candidateIds.map((candidateId) =>
      this.sendCandidateEmail(jobId, candidateId, emailData, config),
    );

    return Promise.all(promises);
  }

  /**
   * Check if candidate can be moved to next stage
   */
  canAdvanceToNextStage(currentStage: string): boolean {
    const stageOrder = [
      "Applied",
      "Resume Review",
      "Shortlisted",
      "Interview Scheduled",
      "Interview Completed",
      "Offer Extended",
      "Hired",
    ];

    return (
      stageOrder.includes(currentStage) &&
      currentStage !== "Hired" &&
      currentStage !== "Rejected"
    );
  }

  /**
   * Get next stage in the workflow
   */
  getNextStage(currentStage: string): string | null {
    const stageOrder = [
      "Applied",
      "Resume Review",
      "Shortlisted",
      "Interview Scheduled",
      "Interview Completed",
      "Offer Extended",
      "Hired",
    ];

    const currentIndex = stageOrder.indexOf(currentStage);
    if (currentIndex === -1 || currentIndex === stageOrder.length - 1) {
      return null;
    }

    return stageOrder[currentIndex + 1];
  }

  /**
   * Validate stage transition
   */
  validateStageTransition(
    fromStage: string,
    toStage: string,
  ): { valid: boolean; error?: string } {
    const stageOrder = [
      "Applied",
      "Resume Review",
      "Shortlisted",
      "Interview Scheduled",
      "Interview Completed",
      "Offer Extended",
      "Hired",
    ];

    // Allow moving to Rejected from any stage
    if (toStage === "Rejected") {
      return { valid: true };
    }

    // Check if stages are valid
    if (!stageOrder.includes(fromStage)) {
      return { valid: false, error: `Invalid current stage: ${fromStage}` };
    }

    if (!stageOrder.includes(toStage)) {
      return { valid: false, error: `Invalid target stage: ${toStage}` };
    }

    // Allow moving forward or backward in the workflow
    return { valid: true };
  }

  /**
   * Get user's videos
   * GET /api/user/videos
   */
  async getUserVideos(): Promise<UserVideo[]> {
    return this.get<UserVideo[]>("/api/user/videos");
  }

  /**
   * Delete a video
   * DELETE /api/user/videos/:id
   */
  async deleteVideo(id: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/api/user/videos/${id}`);
  }

  // ============================================================================
  // YouTube Video Integration
  // ============================================================================

  /**
   * Get user's YouTube videos
   * GET /api/user/youtube/videos
   */
  async getYouTubeVideos(maxResults?: number): Promise<YouTubeVideo[]> {
    const params = maxResults ? { max_results: maxResults } : {};
    return this.get<YouTubeVideo[]>("/api/user/youtube/videos", { params });
  }

  /**
   * Link a YouTube video to user profile
   * POST /api/user/videos/youtube
   */
  async linkYouTubeVideo(request: LinkYouTubeVideoRequest): Promise<UserVideo> {
    return this.post<UserVideo>("/api/user/videos/youtube", request);
  }
}
